'use client';
import { useState, useMemo } from 'react';
import { ScoredStock } from '@/lib/compute';
import { compositeScore, prodQuality } from '@/lib/composite-score';
import { useResource } from '@/lib/use-resource';
import { getSectorColor } from './ControlPanel';

interface TechnicalEntry {
  symbol: string;
  technicalScore: number;
  stochRsi?: number | null;
  macdHistogram?: number | null;
}

interface Props {
  stocks: ScoredStock[];
  pinnedSymbols: Set<string>;
  onTogglePin: (symbol: string) => void;
  onSelectStock: (symbol: string) => void;
  highlightSymbol: string;
  selectedSymbol: string | null;
  technicalData: TechnicalEntry[];
  technicalMonthlyData: TechnicalEntry[];
}

type SortKey = 'symbol' | 'name' | 'sector' | 'marketCap' | 'qualityScore' | 'valuationScore' | 'composite' | 'threeYrUpside' | 'profitability' | 'growth' | 'safety' | 'shareholderReturns' | 'stTech' | 'ltTech';

function fmt(v: number | null, d = 2): string {
  if (v === null || v === undefined) return '--';
  return v.toFixed(d);
}

function fmtCap(v: number): string {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
}

const COLUMNS: { key: SortKey; label: string; width: string }[] = [
  { key: 'symbol', label: 'Symbol', width: 'w-[60px]' },
  { key: 'name', label: 'Name', width: 'w-[160px]' },
  { key: 'sector', label: 'Sector', width: 'w-[110px]' },
  { key: 'marketCap', label: 'Mkt Cap', width: 'w-[80px]' },
  { key: 'qualityScore', label: 'Quality', width: 'w-[68px]' },
  { key: 'profitability', label: 'Prof', width: 'w-[56px]' },
  { key: 'growth', label: 'Growth', width: 'w-[56px]' },
  { key: 'safety', label: 'Safety', width: 'w-[56px]' },
  { key: 'shareholderReturns', label: 'ShrR', width: 'w-[56px]' },
  { key: 'valuationScore', label: 'Value', width: 'w-[68px]' },
  { key: 'composite', label: 'Composite', width: 'w-[72px]' },
  { key: 'threeYrUpside', label: '3Y Tgt %', width: 'w-[72px]' },
  { key: 'stTech', label: 'ST Tech', width: 'w-[60px]' },
  { key: 'ltTech', label: 'LT Tech', width: 'w-[60px]' },
];

export function DataTable({ stocks, pinnedSymbols, onTogglePin: _onTogglePin, onSelectStock, highlightSymbol, selectedSymbol, technicalData, technicalMonthlyData }: Props) {
  // Default sort: Composite, desc. Steve directive 2026-05-11 — composite is
  // the headline ranking number; sorting by quality alone misses the V signal.
  const [sortKey, setSortKey] = useState<SortKey>('composite');
  const [sortAsc, setSortAsc] = useState(false);

  // Target prices for the 3-Yr Tgt % column. Cached + dedup'd via useResource
  // so the StockDetail popup shares this fetch (both consume /api/target-prices).
  const { data: targetPricesRaw } = useResource<{ stocks?: Array<{
    symbol: string;
    currentPrice?: number | null;
    threeYearTarget?: number | null;
  }> }>('/api/target-prices');

  const techMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of technicalData) m.set(t.symbol, t.technicalScore);
    return m;
  }, [technicalData]);

  const techMonthlyMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of technicalMonthlyData) m.set(t.symbol, t.technicalScore);
    return m;
  }, [technicalMonthlyData]);

  const threeYrUpsideMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of targetPricesRaw?.stocks ?? []) {
      if (r.threeYearTarget != null && r.currentPrice != null && r.currentPrice > 0) {
        m.set(r.symbol, r.threeYearTarget / r.currentPrice - 1);
      }
    }
    return m;
  }, [targetPricesRaw]);

  function getValue(s: ScoredStock, key: SortKey): number | string {
    switch (key) {
      case 'symbol': return s.symbol;
      case 'name': return s.name;
      case 'sector': return s.sector;
      case 'marketCap': return s.marketCap;
      case 'qualityScore': return prodQuality(s) ?? -Infinity;
      case 'valuationScore': return s.valuationScore ?? -Infinity;
      case 'composite': return compositeScore(prodQuality(s), s.valuationScore) ?? -Infinity;
      case 'threeYrUpside': return threeYrUpsideMap.get(s.symbol) ?? -Infinity;
      case 'profitability': return s.pillarScores.profitability ?? -Infinity;
      case 'growth': return s.pillarScores.growth ?? -Infinity;
      case 'safety': return s.pillarScores.safety ?? -Infinity;
      case 'shareholderReturns': return s.pillarScores.shareholderReturns ?? -Infinity;
      case 'stTech': return techMap.get(s.symbol) ?? -Infinity;
      case 'ltTech': return techMonthlyMap.get(s.symbol) ?? -Infinity;
    }
  }

  const sorted = useMemo(() => {
    return [...stocks].sort((a, b) => {
      const va = getValue(a, sortKey);
      const vb = getValue(b, sortKey);
      if (typeof va === 'string' && typeof vb === 'string') {
        return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortAsc ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocks, sortKey, sortAsc, techMap, techMonthlyMap, threeYrUpsideMap]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const scoreColor = (v: number | null): string => {
    if (v === null) return '#475569';
    if (v >= 1.5) return '#22c55e';
    if (v >= 0.5) return '#4ade80';
    if (v >= -0.5) return '#94a3b8';
    if (v >= -1.5) return '#f97316';
    return '#ef4444';
  };

  const techColor = (v: number | null): string => {
    if (v === null || v === undefined) return '#475569';
    if (v > 0.5) return '#22c55e';
    if (v > -0.5) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="flex-1 overflow-auto" style={{ background: '#0a0c10' }}>
      <table className="w-full border-collapse table-fixed" style={{ fontFamily: 'var(--font-mono), ui-monospace, monospace' }}>
        <thead className="sticky top-0 z-10">
          <tr style={{ background: '#0d1117' }}>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                onClick={() => handleSort(col.key)}
                className={`px-1.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wider cursor-pointer
                           select-none border-b border-[#1e293b] hover:text-white transition-colors duration-150
                           ${col.key === sortKey ? 'text-blue-400' : 'text-[#e2e8f0]'} ${col.width}`}
              >
                <span className="flex items-center gap-1">
                  {col.label}
                  {col.key === sortKey && (
                    <span className="text-[8px]">{sortAsc ? '\u25B2' : '\u25BC'}</span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(s => {
            const isPinned = pinnedSymbols.has(s.symbol);
            const isHighlighted = highlightSymbol && s.symbol === highlightSymbol;
            const isSelected = selectedSymbol === s.symbol;
            const stScore = techMap.get(s.symbol) ?? null;
            const ltScore = techMonthlyMap.get(s.symbol) ?? null;
            return (
              <tr
                key={s.symbol}
                onClick={() => onSelectStock(s.symbol)}
                className={`cursor-pointer border-b border-[#1e293b]/50 transition-colors duration-100
                           ${isSelected ? 'bg-blue-500/[0.12]' : isPinned ? 'bg-blue-500/[0.08]' : isHighlighted ? 'bg-white/[0.04]' : 'hover:bg-white/[0.03]'}`}
              >
                <td className="px-1.5 py-1.5 text-[11px] font-bold text-white">
                  {isPinned && <span className="text-blue-400 mr-0.5">{'\u2022'}</span>}
                  {s.symbol}
                </td>
                <td className="px-1.5 py-1 text-[10px] text-[#e2e8f0] truncate max-w-[160px]" title={s.name}>{s.name}</td>
                <td className="px-1.5 py-1.5 text-[10px]">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getSectorColor(s.sector) }} />
                    <span className="text-[#e2e8f0]">{s.sector}</span>
                  </span>
                </td>
                <td className="px-1.5 py-1.5 text-[10px] text-[#e2e8f0] tabular-nums">{fmtCap(s.marketCap)}</td>
                <td className="px-1.5 py-1.5 text-[11px] font-semibold tabular-nums" style={{ color: scoreColor(prodQuality(s)) }}>
                  {fmt(prodQuality(s))}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] tabular-nums" style={{ color: scoreColor(s.pillarScores.profitability) }}>
                  {fmt(s.pillarScores.profitability, 1)}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] tabular-nums" style={{ color: scoreColor(s.pillarScores.growth) }}>
                  {fmt(s.pillarScores.growth, 1)}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] tabular-nums" style={{ color: scoreColor(s.pillarScores.safety) }}>
                  {fmt(s.pillarScores.safety, 1)}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] tabular-nums" style={{ color: scoreColor(s.pillarScores.shareholderReturns) }}>
                  {fmt(s.pillarScores.shareholderReturns, 1)}
                </td>
                <td className="px-1.5 py-1.5 text-[11px] font-semibold tabular-nums" style={{ color: scoreColor(s.valuationScore) }}>
                  {fmt(s.valuationScore)}
                </td>
                {(() => {
                  const comp = compositeScore(prodQuality(s), s.valuationScore);
                  return (
                    <td className="px-1.5 py-1.5 text-[11px] font-bold tabular-nums" style={{ color: scoreColor(comp) }}>
                      {fmt(comp)}
                    </td>
                  );
                })()}
                {(() => {
                  const upside = threeYrUpsideMap.get(s.symbol);
                  if (upside == null) {
                    return <td className="px-1.5 py-1.5 text-[10px] text-[#475569]">—</td>;
                  }
                  const pct = upside * 100;
                  const tone = pct >= 50 ? '#22c55e' : pct >= 20 ? '#4ade80' : pct >= 0 ? '#94a3b8' : '#ef4444';
                  return (
                    <td className="px-1.5 py-1.5 text-[10px] font-semibold tabular-nums" style={{ color: tone }}>
                      {pct >= 0 ? '+' : ''}{pct.toFixed(0)}%
                    </td>
                  );
                })()}
                <td className="px-1.5 py-1.5 text-[10px] font-semibold tabular-nums" style={{ color: techColor(stScore) }}>
                  {fmt(stScore)}
                </td>
                <td className="px-1.5 py-1.5 text-[10px] font-semibold tabular-nums" style={{ color: techColor(ltScore) }}>
                  {fmt(ltScore)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
